// Date helpers shared by the API and the SPA.
//
// Two different jobs live here, and mixing them up is the classic source of
// off-by-one-day bugs:
//
//   * localCalendarDate() buckets a stored instant into a calendar day in a
//     SPECIFIC IANA timezone (the household's). Server-side code must use this
//     one, because a Lambda's local time is UTC.
//   * localDateStr()/todayStr()/daysAgoStr() work in the RUNTIME's local zone.
//     They exist for date-picker defaults in the browser, where the runtime zone
//     is the user's zone by definition. Ported from MedicalAssistant's
//     SuggestionCard.tsx:74-87.

/** A bare calendar date with no time or timezone: "YYYY-MM-DD". */
export type CalendarDate = string;

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Calendar date ("YYYY-MM-DD") of an instant, as seen in `timezone`.
 *
 * en-CA is deliberate: it is the locale whose short date format already is
 * ISO-ordered, so no part re-assembly is needed. Falls back to the instant's
 * own date portion if the zone is unknown, which is wrong by at most a day and
 * still sorts correctly.
 */
export function localCalendarDate(isoInstant: string, timezone: string): CalendarDate {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(isoInstant));
  } catch {
    return isoInstant.slice(0, 10);
  }
}

/** Calendar date of a Date in the RUNTIME's local zone (browser use). */
export function localDateStr(d: Date): CalendarDate {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Today in the RUNTIME's local zone (browser use). */
export function todayStr(): CalendarDate {
  return localDateStr(new Date());
}

/**
 * `n` days before today in the RUNTIME's local zone (browser use).
 *
 * Date.setDate() handles month and year rollover, and because it operates on
 * local wall-clock components it lands on the same clock time on the target
 * day even across a DST transition, which is what a date picker wants.
 */
export function daysAgoStr(n: number): CalendarDate {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `date` (YYYY-MM-DD) shifted by `offsetDays`, via a Date.UTC walk — never
 * local Date construction, so a DST transition never shifts a day boundary.
 * Copied from MedicalAssistant `itemStats.ts:16-23`.
 */
export function utcDayWalk(date: CalendarDate, offsetDays: number): CalendarDate {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
}

const WEEKDAY_SHORT: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Local weekday of an instant in `timezone`, matching Date#getDay
 * (0 = Sunday ... 6 = Saturday). Used by the digest cron to honor
 * `weeklyDigestDay` without depending on the Lambda's UTC clock.
 */
export function localWeekday(isoInstant: string, timezone: string): number {
  try {
    const short = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
    }).format(new Date(isoInstant));
    return WEEKDAY_SHORT[short] ?? 0;
  } catch {
    return new Date(isoInstant).getUTCDay();
  }
}

/**
 * Local hour (0-23) of an instant in `timezone`. Used by the nudge cron
 * to honor `overdueNudgeHour`.
 */
export function localHour(isoInstant: string, timezone: string): number {
  try {
    const hour = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date(isoInstant));
    const n = Number.parseInt(hour, 10);
    if (!Number.isFinite(n)) return 0;
    return n === 24 ? 0 : n;
  } catch {
    return new Date(isoInstant).getUTCHours();
  }
}

/** Whole calendar days from `a` to `b` (b - a), both YYYY-MM-DD. */
export function daysBetween(a: CalendarDate, b: CalendarDate): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (Date.UTC(by ?? 0, (bm ?? 1) - 1, bd ?? 1) - Date.UTC(ay ?? 0, (am ?? 1) - 1, ad ?? 1)) / DAY_MS,
  );
}
